import {
  applicationSnapshotToFields,
  briefingSnapshotToFields,
  submitPartialDraft,
} from './formPartialService';

/** Zoho requires both name parts; single-token names reuse first in last (see zoho-post-out.txt). */
function splitZohoName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  const firstName = parts[0];
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : firstName;
  return { firstName, lastName };
}

function appendIfFilled(target, key, value) {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    target[key] = String(value).trim();
  }
}

function partialPayloadHasFields(mappedData) {
  return Object.entries(mappedData).some(
    ([k, v]) => !k.startsWith('_') && v != null && String(v).trim() !== '',
  );
}

function ensurePartialZohoNames(mappedData, email) {
  if (mappedData.Name_First && mappedData.Name_Last) return mappedData;
  const derived = (email || '').split('@')[0] || 'Applicant';
  if (!mappedData.Name_First) mappedData.Name_First = derived;
  if (!mappedData.Name_Last) mappedData.Name_Last = mappedData.Name_First;
  return mappedData;
}

/** Canonical Zoho Radio1 values — must match options configured in Zoho Forms. */
const INCOME_MAP = {
  '<10L': 'Less than 10L',
  'Less than 10L': 'Less than 10L',
  '10-20L': '10L - 20L',
  '10L-20L': '10L - 20L',
  '10L - 20L': '10L - 20L',
  '20-30L': '20L - 30L',
  '20L-30L': '20L - 30L',
  '20L - 30L': '20L - 30L',
  '30-50L': '30L - 50L',
  '30L-50L': '30L - 50L',
  '30L - 50L': '30L - 50L',
  '50L-1Cr': '50L - 1Cr',
  '50L - 1Cr': '50L - 1Cr',
  '>1Cr': 'Above 1Cr',
  'Above 1Cr': 'Above 1Cr',
};

function mapIncomeToZoho(value) {
  const v = (value || '').trim();
  if (!v) return '';
  return INCOME_MAP[v] || v;
}

const ZOHO_FORMS = {
  application: {
    url: 'https://forms.zohopublic.in/ironlady/form/100BMApplicationWEB/formperma/M47K-MehrAgjOVZx2c1-RuEQ6Nz8OLl67gR-La7wDeY/htmlRecords/submit',
    mapData: (formData) => {
      const isPartial = !!formData._partial;
      const { firstName, lastName } = splitZohoName(formData.fullName);

      const experienceMap = {
        '8-10': '8-10 years',
        '11-15': '11-15 years',
        '15+': '15+ years',
      };

      const scopeMap = {
        'individual': 'Individual contributor',
        'small-team': 'Small team',
        'team-function': 'Leading team/function',
        'cross-functional': 'Cross-functional',
        'pnl': 'P&L accountability',
        'others': 'Others',
      };

      const email = (formData.email || '').trim();
      const phone = (formData.phone || '').trim();
      const designation = (formData.designation || '').trim();
      const organisation = (formData.organisation || '').trim();
      const industry = (formData.industry || '').trim();
      const q7 = (formData.q7 || '').trim();
      const q8 = (formData.q8 || '').trim();
      const q9 = (formData.q9 || '').trim();

      const expVal = experienceMap[formData.experience] || formData.experience || '';
      const incVal = mapIncomeToZoho(formData.income);
      const scopeVal = scopeMap[formData.scope] || formData.scope || '';

      if (isPartial) {
        const o = {};
        if ((formData.fullName || '').trim()) {
          const { firstName: fn, lastName: ln } = splitZohoName(formData.fullName);
          if (fn) {
            o.Name_First = fn;
            o.Name_Last = ln;
          }
        }
        appendIfFilled(o, 'Email', email);
        appendIfFilled(o, 'PhoneNumber_countrycode', phone);
        appendIfFilled(o, 'SingleLine', designation);
        appendIfFilled(o, 'SingleLine1', organisation);
        appendIfFilled(o, 'SingleLine2', industry);
        if (formData.experience) {
          o.Radio = experienceMap[formData.experience] || formData.experience;
        }
        if (formData.income) {
          o.Radio1 = mapIncomeToZoho(formData.income);
        }
        if (formData.scope) {
          o.Radio2 = scopeMap[formData.scope] || formData.scope;
        }
        appendIfFilled(o, 'MultiLine', q7);
        appendIfFilled(o, 'MultiLine1', q8);
        appendIfFilled(o, 'MultiLine2', q9);
        // Zoho often rejects rows with no email; only add when there is real user data elsewhere.
        if (partialPayloadHasFields(o) && !o.Email) {
          o.Email = 'incomplete.partial@example.com';
        }
        return ensurePartialZohoNames(o, email);
      }

      return {
        Name_First: firstName,
        Name_Last: lastName,
        Email: email,
        PhoneNumber_countrycode: phone,
        SingleLine: designation,
        SingleLine1: organisation,
        SingleLine2: industry,
        Radio: expVal,
        Radio1: incVal,
        Radio2: scopeVal,
        MultiLine: q7,
        MultiLine1: q8,
        MultiLine2: q9,
      };
    },
  },
  briefing: {
    url: 'https://forms.zohopublic.in/ironlady/form/100BMExecutiveBriefingWeb/formperma/Z-7WX_6jvBSiDSW3ETJN-TLoKaepS75c_oaEFFyqOm4/htmlRecords/submit',
    mapData: (formData) => {
      const isPartial = !!formData._partial;
      const { firstName, lastName } = splitZohoName(formData.fullName);

      const experienceMap = {
        '8-10': '8-10 years',
        '11-15': '11-15 years',
        '15+': '15+ years',
      };

      const sourceMap = {
        'instagram-linkedin': 'Instagram/LinkedIn',
        'referral': 'Referral',
        'google-search': 'Google/Search',
        'community': 'Community',
        'other': 'Other',
      };

      const email = (formData.email || '').trim();
      const phone = (formData.phone || '').trim();
      const city = (formData.city || '').trim();
      const designation = (formData.designation || '').trim();
      const industry = (formData.industry || '').trim();
      const objectives = (formData.objectives || '').trim();

      const expVal = experienceMap[formData.experience] || formData.experience || '';
      const incVal = mapIncomeToZoho(formData.income);
      const srcVal = sourceMap[formData.source] || formData.source || '';

      if (isPartial) {
        const o = {};
        if ((formData.fullName || '').trim()) {
          const { firstName: fn, lastName: ln } = splitZohoName(formData.fullName);
          if (fn) {
            o.Name_First = fn;
            o.Name_Last = ln;
          }
        }
        appendIfFilled(o, 'Email', email);
        appendIfFilled(o, 'PhoneNumber_countrycode', phone);
        appendIfFilled(o, 'SingleLine', city);
        appendIfFilled(o, 'SingleLine1', designation);
        appendIfFilled(o, 'SingleLine2', industry);
        if (formData.experience) {
          o.Radio = experienceMap[formData.experience] || formData.experience;
        }
        if (formData.income) {
          o.Radio1 = mapIncomeToZoho(formData.income);
        }
        appendIfFilled(o, 'MultiLine', objectives);
        if (formData.source) {
          o.Radio2 = sourceMap[formData.source] || formData.source;
        }
        if (partialPayloadHasFields(o) && !o.Email) {
          o.Email = 'incomplete.partial@example.com';
        }
        return ensurePartialZohoNames(o, email);
      }

      return {
        Name_First: firstName,
        Name_Last: lastName,
        Email: email,
        PhoneNumber_countrycode: phone,
        SingleLine: city,
        SingleLine1: designation,
        SingleLine2: industry,
        Radio: expVal,
        Radio1: incVal,
        MultiLine: objectives,
        Radio2: srcVal,
      };
    },
  },
};

function mappedDataToUrlSearchParams(mappedData) {
  const body = new URLSearchParams();
  body.append('zf_referrer_name', window.location.href);
  body.append('zf_redirect_url', '');
  body.append('zc_gad', '');
  Object.entries(mappedData).forEach(([key, value]) => {
    if (key.startsWith('_')) return;
    if (value === undefined || value === null) return;
    const s = String(value);
    if (s.trim() === '') return;
    body.append(key, s);
  });
  return body;
}

function appendMappedFields(form, mappedData) {
  const addField = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addField('zf_referrer_name', window.location.href);
  addField('zf_redirect_url', '');
  addField('zc_gad', '');

  Object.entries(mappedData).forEach(([key, value]) => {
    if (key.startsWith('_')) return;
    if (value === undefined || value === null) return;
    const s = String(value);
    if (s.trim() === '') return;
    addField(key, s);
  });
}

/** fetch keepalive / sendBeacon — survives tab close on mobile (iframe is killed on unload). */
export function postMappedToZohoKeepalive(url, mappedData) {
  if (!url || !partialPayloadHasFields(mappedData)) return false;
  const body = mappedDataToUrlSearchParams(mappedData).toString();
  let sent = false;
  if (navigator.sendBeacon) {
    sent = navigator.sendBeacon(
      url,
      new Blob([body], { type: 'application/x-www-form-urlencoded;charset=UTF-8' }),
    );
  }
  if (!sent) {
    try {
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        keepalive: true,
        mode: 'no-cors',
      });
      sent = true;
    } catch (e) {
      sent = false;
    }
  }
  return sent;
}

/** Hidden iframe POST — reliable while the page is still open. */
export function postMappedToZohoIframe(url, mappedData) {
  if (!url || !partialPayloadHasFields(mappedData)) return false;

  const iframeName = `zoho_partial_${Date.now()}`;
  const iframe = document.createElement('iframe');
  iframe.name = iframeName;
  iframe.style.cssText = 'display:none;width:0;height:0;border:0';
  document.body.appendChild(iframe);

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.target = iframeName;
  form.style.display = 'none';
  form.acceptCharset = 'UTF-8';
  appendMappedFields(form, mappedData);
  document.body.appendChild(form);
  form.submit();

  setTimeout(() => {
    try { form.remove(); } catch (e) { /* ignore */ }
    try { iframe.remove(); } catch (e) { /* ignore */ }
  }, 10000);
  return true;
}

/** localStorage session TTL for last-hash dedup. */
export const PARTIAL_WINDOW_MS = 60 * 60 * 1000;

function partialWindowStorageKey(formType, email) {
  return `il_zoho_partial_window_${formType}_${(email || '').trim().toLowerCase()}`;
}

export function readPartialWindow(formType, email) {
  try {
    const raw = localStorage.getItem(partialWindowStorageKey(formType, email));
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.windowStart || Date.now() - session.windowStart > PARTIAL_WINDOW_MS) {
      return null;
    }
    return session;
  } catch (e) {
    return null;
  }
}

function writePartialWindow(formType, email, session) {
  try {
    localStorage.setItem(partialWindowStorageKey(formType, email), JSON.stringify(session));
  } catch (e) {
    /* ignore */
  }
}

/** Drop browser partial-sync state after a full application submit. */
export function clearPartialTracking(formType, email) {
  const em = (email || '').trim().toLowerCase();
  if (!em) return;
  try {
    localStorage.removeItem(partialWindowStorageKey(formType, em));
  } catch (e) {
    /* ignore */
  }
}

/** Skip only exact duplicate payloads (partial sync runs on form close). */
export function evaluatePartialZohoSend(formType, email, hash) {
  const now = Date.now();
  const existing = readPartialWindow(formType, email);

  if (!existing) {
    return {
      send: true,
      session: { windowStart: now, lastHash: hash },
      reason: 'new_window',
    };
  }

  if (hash === existing.lastHash) {
    return { send: false, session: existing, reason: 'unchanged' };
  }

  return { send: true, session: existing, reason: 'changed' };
}

function markPartialZohoPosted(formType, email, session, hash) {
  writePartialWindow(formType, email, {
    ...session,
    windowStart: session.windowStart || Date.now(),
    lastHash: hash,
    lastZohoAt: Date.now(),
  });
}

export function submitToZoho(formType, formData) {
  return new Promise((resolve, reject) => {
    const config = ZOHO_FORMS[formType];
    if (!config || !config.url) {
      reject(new Error(`Zoho form "${formType}" is not configured.`));
      return;
    }

    const mappedData = config.mapData(formData);
    if (formData._partial && !partialPayloadHasFields(mappedData)) {
      resolve({ success: true, skipped: true });
      return;
    }

    const iframeName = `zoho_submit_${formType}_${Date.now()}`;
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.style.cssText = 'display:none;width:0;height:0;border:0';
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.url;
    form.target = iframeName;
    form.style.display = 'none';
    form.acceptCharset = 'UTF-8';
    appendMappedFields(form, mappedData);
    document.body.appendChild(form);

    let resolved = false;
    let submitted = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      if (!formData._partial) {
        clearPartialTracking(formType, formData.email);
      }
      resolve({ success: true });
      setTimeout(() => {
        try { form.remove(); } catch (e) { /* ignore */ }
        try { iframe.remove(); } catch (e) { /* ignore */ }
      }, 10000);
    };

    iframe.onload = () => {
      if (!submitted) return;
      done();
    };
    iframe.onerror = done;
    setTimeout(done, 8000);

    form.submit();
    submitted = true;
  });
}

export function partialSnapshotHash(formData) {
  const copy = { ...formData };
  delete copy._partial;
  return Object.keys(copy)
    .sort()
    .map((k) => `${k}=${copy[k]}`)
    .join('|');
}

/** Partial on close → API; server owns a single Zoho Forms row per email. */
export async function submitPartialToZoho(formType, formData) {
  const fields =
    formType === 'application'
      ? applicationSnapshotToFields(formData)
      : briefingSnapshotToFields(formData);
  if (!Object.keys(fields).length) return { skipped: true };
  return submitPartialDraft({ formType, fields, useBeacon: false });
}

export function submitPartialBeacon(formType, formData) {
  const fields =
    formType === 'application'
      ? applicationSnapshotToFields(formData)
      : briefingSnapshotToFields(formData);
  if (!Object.keys(fields).length) return;
  void submitPartialDraft({ formType, fields, useBeacon: true });
}

/** Last resort when API partial save fails on tab close (may duplicate if API actually saved). */
export function submitPartialEmergencyToZoho(formType, apiFields) {
  const config = ZOHO_FORMS[formType];
  if (!config?.url || !apiFields) return false;

  const formData = { _partial: true };
  if (formType === 'application') {
    formData.fullName = apiFields.Name || '';
    formData.email = apiFields.Email || '';
    formData.phone = apiFields.Phone || '';
    formData.designation = apiFields.Designation || '';
    formData.organisation = apiFields.Organisation || '';
    formData.industry = apiFields.Industry || '';
    formData.experience = apiFields.Experience || '';
    formData.income = apiFields['Income Range'] || '';
    formData.scope = apiFields['Scope of Responsibility'] || '';
    formData.q7 = apiFields['Why Applying'] || '';
    formData.q8 = apiFields['3-Year Vision'] || '';
    formData.q9 = apiFields['Top Challenges'] || '';
  } else {
    formData.fullName = apiFields.Name || '';
    formData.email = apiFields.Email || '';
    formData.phone = apiFields.Phone || '';
    formData.city = apiFields.City || '';
    formData.designation = apiFields.Designation || '';
    formData.industry = apiFields.Industry || '';
    formData.experience = apiFields.Experience || '';
    formData.income = apiFields['Income Range'] || '';
    formData.objectives = apiFields.Objectives || '';
    formData.source = apiFields.Source || '';
  }

  const mappedData = config.mapData(formData);
  return postMappedToZohoKeepalive(config.url, mappedData);
}
